import { Chip, Tooltip } from '@heroui/react';

import {
  DeploymentInfo,
  buildDeploymentSummaryInfos
} from './deploymentSummaryInfos';
import { useIsAutoscaling } from '../../../hooks/useIsAutoscaling';
import { isAutoScalingDeploy } from '../../../utils/isAutoScalingDeploy';

import i18n from '~/common/helpers/i18n';
import { Deployment } from '~/entities/Deployment';
import {
  getMessageStatus,
  type DataMessageType
} from '~/scenes/Runtime/components/StatusDeploy';

type DeploymentSummarySectionProps = {
  deployment: Deployment;
};

/** Beehive `Tag` variants do not exist in HeroUI — this is the same severity, in HeroUI colours. */
const CHIP_COLOR: Record<
  string,
  'default' | 'primary' | 'success' | 'warning' | 'danger'
> = {
  success: 'success',
  danger: 'danger',
  warning: 'warning',
  info: 'primary',
  neutral: 'default'
};

const normalizeSizeTitle = (name?: string) => {
  if (!name) return '';
  return name.at(0)?.toUpperCase();
};

/**
 * One label/value pair. The beehive sidesheet ran these as flat lines of running text, which read
 * as a single paragraph of grey; the label above its value is how the agent log drawer states the
 * same kind of field.
 */
const InfoPair = ({ info }: { info: DeploymentInfo }) => {
  const label = (
    <dt className='truncate text-label-md text-on-surface-variant'>
      {info.title}
    </dt>
  );

  const value = (
    <dd className='truncate text-body-regular-md text-on-surface'>
      {info.value}
    </dd>
  );

  return (
    <div className='flex min-w-0 items-start gap-2'>
      {info.icon && (
        <info.icon className='icon-smallest mt-1 shrink-0 text-on-surface-variant' />
      )}
      <div className='min-w-0 flex-1'>
        {info.title &&
          (info.titleTooltip ? (
            <Tooltip content={info.titleTooltip}>{label}</Tooltip>
          ) : (
            label
          ))}
        {info.valueTooltip ? (
          <Tooltip content={info.valueTooltip}>{value}</Tooltip>
        ) : (
          value
        )}
      </div>
    </div>
  );
};

type MetricProps = {
  name: string;
  nameTooltip?: string | null;
  value?: string | number;
};

/**
 * The HeroUI counterpart of `CardRuntime`'s `Details` tile, in the shape Governance gives its
 * consumption numbers: micro-caps label over a mono figure, in a bordered card.
 */
const Metric = ({ name, nameTooltip, value }: MetricProps) => {
  const label = (
    <span className='truncate text-center text-xs font-semibold uppercase text-on-surface-variant'>
      {name}
    </span>
  );

  return (
    <div className='flex flex-col items-center justify-center gap-0.5 rounded-lg border border-solid border-outline px-2 py-2.5'>
      {nameTooltip ? <Tooltip content={nameTooltip}>{label}</Tooltip> : label}
      <span className='font-mono text-2xl font-semibold tracking-[.5px] text-on-surface'>
        {value}
      </span>
    </div>
  );
};

/**
 * Pipeline name, tags, the deployment metadata and the replica counters — everything
 * `PipelineDeployWithServerless` rendered at the top of the beehive sidesheet, laid out the way
 * the portal's other HeroUI detail drawer lays out a header plus a panel of fields.
 */
export const DeploymentSummarySection = ({
  deployment
}: DeploymentSummarySectionProps) => {
  const dataMessage: DataMessageType = getMessageStatus(deployment as never);
  const showNewAutoScalingExperience = useIsAutoscaling();
  const showAutoScalingValues =
    isAutoScalingDeploy({
      minReplicaCount: deployment?.activeConfiguration?.minReplicaCount,
      replicaCount: deployment?.activeConfiguration?.replicaCount
    }) && showNewAutoScalingExperience;

  const infos = buildDeploymentSummaryInfos(deployment)
    .flat()
    .filter(info => Boolean(info.value));

  return (
    <>
      {/* The pipeline is the subject of the drawer, so it names the content instead of sitting in
          a panel of its own — the way the capsule sidesheet leads with the capsule. */}
      <div className='flex flex-col gap-2'>
        <h3 className='break-words text-title-md text-on-surface'>
          {deployment?.pipeline?.name}
        </h3>
        <div className='flex flex-wrap items-center gap-2'>
          <Chip
            size='sm'
            radius='sm'
            className='bg-surface-dim text-label-md text-on-surface'
          >
            {`${i18n.t('scenes.runtime.labels.version')} ${
              deployment?.pipeline?.versionMajor
            }.${deployment?.pipeline?.versionMinor}`}
          </Chip>
          {!deployment?.isHistory && (
            <Chip
              size='sm'
              radius='sm'
              variant='flat'
              color={CHIP_COLOR[dataMessage.style] ?? 'default'}
            >
              {dataMessage.text}
            </Chip>
          )}
          {showAutoScalingValues && (
            <Chip size='sm' radius='sm' variant='flat' color='primary'>
              Autoscaling
            </Chip>
          )}
        </div>
      </div>

      <section className='rounded-lg border border-solid border-outline px-4 py-3.5'>
        <dl className='grid gap-x-6 gap-y-3 sm:grid-cols-2'>
          {infos.map(info => (
            <InfoPair key={`${info.title}-${info.value}`} info={info} />
          ))}
        </dl>
      </section>

      {/* Their own row of cards, not cards inside the panel above: a bordered tile inside a
          bordered panel reads as a mistake. */}
      <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
        <Metric
          name={i18n.t('scenes.runtime.labels.deploy_size') || ''}
          value={normalizeSizeTitle(deployment?.activeConfiguration?.name)}
        />
        <Metric
          name={i18n.t('scenes.runtime.labels.max_executions') || ''}
          nameTooltip={i18n.t('scenes.runtime.labels.max_executions_tooltip')}
          value={deployment?.activeConfiguration?.actualConsumers}
        />
        {showAutoScalingValues && (
          <Metric
            name={i18n.t('scenes.runtime.labels.minimum_replicas') || ''}
            value={deployment?.activeConfiguration?.minReplicaCount}
          />
        )}
        {!showAutoScalingValues && Boolean(deployment?.availableReplicas) && (
          <Metric
            name={i18n.t('scenes.runtime.labels.running_replicas') || ''}
            value={deployment?.availableReplicas?.split('/')[0]}
          />
        )}
        <Metric
          name={
            showAutoScalingValues
              ? i18n.t('scenes.runtime.labels.maximum_replicas') || ''
              : i18n.t('scenes.runtime.labels.target_of_replicas') || ''
          }
          value={deployment?.activeConfiguration?.replicaCount}
        />
      </div>
    </>
  );
};

export default DeploymentSummarySection;
